import { TableColumn } from "react-data-table-component";
import { Fragment, useState } from "react";
import { Badges, Btn } from "../../AbstractElements";
import { RefreshCcw } from "react-feather";
import { CustomCellInterFace } from "../../Types/TablesType";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import Swal from "sweetalert2";

//Modals
import UpdateClusterModal from "../../Components/UiKits/Modal/Cluster/Update";

//Services
import {
  VIEW_CLUSTER_QUERY_KEY,
  CLUSTER_QUERY_KEY,
  clusterService,
} from "../../Services/clusterService";
import {
  VIEW_DEPSITE_QUERY_KEY,
  DEPSITE_QUERY_KEY,
  deploymentsiteService,
} from "../../Services/deploymentsiteService";

//Types
import { ViewCluster } from "../../Types/CompanyProfile/Cluster";

const ActionCell = ({ row }: { row: ViewCluster }) => {
  const queryClient = useQueryClient();
  const [isRowLoading, setIsRowLoading] = useState<boolean>(false);
  const QueryKey = [...VIEW_CLUSTER_QUERY_KEY, row.company_code];
  const handleStatusToggle = () => {
    Swal.fire({
      icon: "warning",
      html: `Are you sure you want to <b>"${
        row.status_label === "ACTIVE" ? "Deactivate " : "Activate"
      }"</b> This Cluster: <b>"${row.name}"</b> ?`,
      showCancelButton: true,
      cancelButtonText: "Cancel",
      cancelButtonColor: "#dc3545",
      confirmButtonText: "Yes",
      confirmButtonColor: "#0d6efd",
    }).then((result) => {
      if (result.isConfirmed) {
        setIsRowLoading(true);
        toggleMutation.mutate(row.id);
      }
    });
  };
  const toggleMutation = useMutation({
    mutationFn: clusterService.toggleStatus,
    onSuccess: async (variables: { id: number; status_label: string }) => {
      //queryClient.invalidateQueries();
      queryClient.invalidateQueries({
        queryKey: [VIEW_DEPSITE_QUERY_KEY, VIEW_CLUSTER_QUERY_KEY],
      });
      queryClient.setQueryData<ViewCluster[]>(QueryKey, (oldData) =>
        oldData?.map((div) =>
          div.id === variables.id
            ? {
                ...div,
                status_label:
                  div.status_label === "ACTIVE" ? "INACTIVE" : "ACTIVE",
              }
            : div,
        ),
      );

      await queryClient.refetchQueries({
        queryKey: QueryKey,
        exact: true,
      });

      toast.success(
        `"${row.name}" status has been set to "${
          variables.status_label === "ACTIVE" ? "INACTIVE" : "ACTIVE"
        }".`,
      );
      setIsRowLoading(false);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || "Failed to toggle status");
      setIsRowLoading(false);
    },
  });

  return (
    <div className="d-flex align-items-center gap-2">
      {/* Update Modal */}
      <UpdateClusterModal id={row.id} />
      {/* StatusToggle Button */}
      <Btn
        size="sm"
        color={row.status_label === "ACTIVE" ? "danger" : "success"}
        className="px-2"
        onClick={handleStatusToggle}
        disabled={isRowLoading}
      >
        {isRowLoading ? (
          <>
            <span
              className="spinner-border spinner-border-sm me-2"
              role="status"
              aria-hidden="true"
            ></span>
            Loading...
          </>
        ) : (
          <>
            <RefreshCcw size={11} className="me-1" />
            {row.status_label === "ACTIVE" ? "Disable" : <>Enable&nbsp;</>}
          </>
        )}
      </Btn>
    </div>
  );
};

const CustomBadge: React.FC<CustomCellInterFace> = ({ label, color }) => (
  <Badges pill color={color}>
    {label}
  </Badges>
);

export const ClusterTableColumn: TableColumn<ViewCluster>[] = [
  {
    name: "REGION",
    selector: (row) => row.name,
    cell: (row) => (
      <div style={{ whiteSpace: "pre-line" }}>{`${row.region_name}`}</div>
    ),
    sortable: true,
    center: false,
  },
  {
    name: "CLUSTER",
    selector: (row) => row.name,
    cell: (row) => (
      <div style={{ whiteSpace: "pre-line" }}>{`${row.name}`}</div>
    ),
    sortable: true,
    center: false,
  },
  {
    name: "STATUS",
    selector: (row) => row.status_label,
    cell: (row) => (
      <>
        <CustomBadge
          color={row.status_label === "ACTIVE" ? "success" : "danger"}
          label={row.status_label}
        />
      </>
    ),
    width: "100px",
    sortable: true,
    center: false,
  },
  {
    name: "DATE CREATED",
    selector: (row) => row.created_date,
    cell: (row) => (
      <div style={{ whiteSpace: "pre-line" }}>
        {row.created_by_user}
        {"\n"}
        {row.created_date}
      </div>
    ),
    width: "180px",
    sortable: true,
    center: false,
  },
  {
    name: "DATE MODIFIED",
    selector: (row) => row.updated_date,
    cell: (row) => (
      <div style={{ whiteSpace: "pre-line" }}>
        {row.updated_by_user}
        {"\n"}
        {row.updated_date}
      </div>
    ),
    width: "180px",
    sortable: true,
    center: false,
  },
  {
    name: "ACTION",
    sortable: false,
    center: false,
    cell: (row) => <ActionCell row={row} />,
    width: "200px",
  },
];
